(() => {
  class WhatsAppOrder extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.variants = JSON.parse(this.querySelector('script').textContent);
      this.link = this.querySelector('a');
      this.form = document.getElementById(this.dataset.form);
      const options = { signal: this.controller.signal };
      const update = () => this.update();
      this.form?.addEventListener('variant:change', update, options);
      this.form?.addEventListener('change', update, options);
      this.link.addEventListener('click', event => {
        this.update();
        if (this.link.getAttribute('aria-disabled') === 'true') event.preventDefault();
      }, options);
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); }
    update() {
      const id = this.form?.elements.namedItem('id')?.value;
      const variant = this.variants.find(value => String(value.id) === String(id));
      const available = Boolean(variant?.available);
      this.link.setAttribute('aria-disabled', String(!available));
      this.link.tabIndex = available ? 0 : -1;
      if (!variant) return;
      const url = new URL(this.dataset.url);
      url.searchParams.set('variant', variant.id);
      const whatsapp = new URL(this.dataset.base);
      whatsapp.searchParams.set('text', `Hello! I'd like to order ${this.dataset.title}.\n${url.href}`);
      this.link.href = whatsapp.href;
    }
  }
  if (!customElements.get('ft-whatsapp-order')) customElements.define('ft-whatsapp-order', WhatsAppOrder);
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-ft-header-search]');
      this.panel = this.querySelector('[data-ft-search-results]');
      this.status = this.querySelector('[data-ft-search-status]');
      this.cache = new Map();
      const options = { signal: this.listeners.signal };
      this.input.addEventListener('input', () => this.schedule(), options);
      this.input.addEventListener('focus', () => {
        if (this.panel.childElementCount && this.input.value.trim().length >= 2) this.open();
      }, options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => { if (!this.contains(document.activeElement)) this.close(); });
      }, options);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.form.addEventListener('submit', () => this.close(), options);
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
    }
    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
    schedule() {
      clearTimeout(this.timer);
      this.request?.abort();
      const query = this.input.value.trim();
      this.panel.replaceChildren();
      this.close();
      this.status.textContent = '';
      if (query.length < 2) return;
      this.timer = setTimeout(() => this.search(query), 180);
    }
    async search(query) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.status.textContent = 'Searching…';
      try {
        let products = this.cache.get(query);
        if (!products) {
          const url = new URL(`${this.form.action.replace(/\/$/, '')}/suggest.json`);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          const response = await fetch(url, { signal: request.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const result = await response.json();
          products = result.resources?.results?.products || [];
          this.cache.set(query, products);
        }
        if (request.signal.aborted || this.input.value.trim() !== query) return;
        this.render(products, query);
        this.status.textContent = products.length ? `${products.length} product suggestions. Use the arrow keys to browse.` : 'No matching products. Try another search.';
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.input.value.trim() !== query) return;
        this.render([], query, true);
        this.status.textContent = 'Press Enter to see search results.';
      }
    }
    render(products, query, failed = false) {
      this.panel.replaceChildren();
      const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      }).format(Number(value));
      for (const product of products) {
        const link = document.createElement('a');
        link.className = 'ft-header-search__result';
        link.href = product.url;
        const source = typeof product.image === 'string' ? product.image : product.featured_image?.url;
        if (source) {
          const img = document.createElement('img');
          img.src = source;
          img.alt = '';
          img.width = img.height = 48;
          link.append(img);
        }
        const details = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title;
        details.append(title);
        if (product.price != null) {
          const price = document.createElement('small');
          price.textContent = `${money(product.price)}${product.available === false ? ' · Sold out' : ''}`;
          details.append(price);
        }
        link.append(details);
        this.panel.append(link);
      }
      if (!products.length && !failed) {
        const empty = document.createElement('p');
        empty.className = 'ft-header-search__empty';
        empty.textContent = 'No matching products. Try another search.';
        this.panel.append(empty);
      }
      const all = document.createElement('a');
      all.className = 'ft-header-search__all';
      const destination = new URL(this.form.action);
      destination.searchParams.set('q', query);
      destination.searchParams.set('type', 'product');
      all.href = destination.href;
      all.textContent = `View all results for “${query}” ›`;
      this.panel.append(all);
      if (this.contains(document.activeElement)) this.open();
    }
    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        clearTimeout(this.timer);
        this.request?.abort();
        this.status.textContent = '';
        this.close();
        this.input.focus({ preventScroll: true });
        // Focusing a dismissed field should not reopen the suggestions.
        this.close();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.panel.hidden) return;
      const links = [...this.panel.querySelectorAll('a')];
      if (!links.length) return;
      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? (current + 1) % links.length : current <= 0 ? links.length - 1 : current - 1;
      links[next].focus();
    }
  }
  if (!customElements.get('ft-header-search')) customElements.define('ft-header-search', HeaderSearch);
  const setupScrollHeader = (scope = document) => {
    const header = scope.querySelector('store-header[data-ft-scroll-header]');
    if (!header || header.dataset.ftScrollReady) return;
    header.dataset.ftScrollReady = 'true';
    const wrap = header.querySelector('ft-header-search');
    const toggle = header.querySelector('[data-ft-search-toggle]');
    let atTop = window.scrollY <= 0;
    let frame;
    const update = () => {
      frame = null;
      atTop = window.scrollY <= 0;
      header.toggleAttribute('data-ft-at-top', atTop);
      const compactCountdown = header.closest('ft-sticky-stack') && window.matchMedia('(max-width: 1149px)').matches;
      const expanded = (!compactCountdown && !atTop) || header.hasAttribute('data-ft-search-open');
      wrap.inert = !expanded;
      toggle.inert = expanded;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute('aria-hidden', String(expanded));
      if (!expanded) wrap.close?.();
    };
    window.addEventListener('scroll', () => {
      if (!frame) frame = requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('pageshow', update);
    toggle.addEventListener('click', () => {
      header.setAttribute('data-ft-search-open', '');
      update();
      wrap.input.focus({ preventScroll: true });
    });
    const dismiss = () => {
      header.removeAttribute('data-ft-search-open');
      update();
    };
    wrap.querySelector('[data-ft-search-close]')?.addEventListener('click', () => {
      dismiss();
      toggle.focus({ preventScroll: true });
    });
    document.addEventListener('pointerdown', event => {
      if (!wrap.contains(event.target) && !toggle.contains(event.target)) dismiss();
    });
    wrap.addEventListener('focusout', () => requestAnimationFrame(() => {
      if (!wrap.contains(document.activeElement)) dismiss();
    }));
    wrap.addEventListener('keydown', event => {
      if (event.key === 'Escape' && (atTop || header.closest('ft-sticky-stack'))) {
        dismiss();
        toggle.focus({ preventScroll: true });
      }
    });
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupScrollHeader(), { once: true });
  else setupScrollHeader();
  document.addEventListener('shopify:section:load', () => setupScrollHeader());
})();
